import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { PratusStepper } from 'components/DesignSystems/PratusStepper/PratusStepper';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import {
  useApproveWithoutReview,
  useRequestApprovals,
} from 'hooks/useApprovals';
import {
  useActualIncidentId,
  useGroupGuid,
} from 'components/IAP/hooks/commonHooks';
import { ITEM_TYPES } from 'constants/itemType';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useIsCommandCenter } from './utils/isCommandCenter';
import { internalLayerVersionsKeys } from 'hooks/internalLayerVersion/queryKeys';
const steps = [
  { id: 1, label: 'Draft Creation' },
  { id: 2, label: 'Incident Commander' },
];

export function RequestApprovalModal({
  version,
  buttonClassName,
  onBack,
}: {
  version?: InternalLayerVersion | null;
  buttonClassName?: string;
  onBack: () => void;
}) {
  const [show, setShow] = useState(false);
  const requestApprovals = useRequestApprovals(internalLayerVersionsKeys.all());
  const approveWithoutReview = useApproveWithoutReview(
    internalLayerVersionsKeys.all()
  );
  const incidentId = useActualIncidentId();
  const groupGuid = useGroupGuid();
  const isCommandCenter = useIsCommandCenter();
  const icsPositions = useGetPositionsForIncident();

  const handleConfirm = async () => {
    if (!version || !version.approval) return;

    // From Commander Center - we bypass approval request and immediately approve
    // From Incident Workspace - we request approval from the Incident Commander
    if (isCommandCenter) {
      await approveWithoutReview.mutateAsync({
        itemId: version.approval.sourceItemId,
        itemType: ITEM_TYPES.InternalLayerVersion,
        groupId: groupGuid,
      });
    } else {
      const incidentCommanderPosition = icsPositions.data?.find(
        (p) => p.name === 'Incident Commander'
      );
      if (!incidentCommanderPosition) {
        console.error(
          `Incident Commander position not found for incident_id: ${incidentId}`
        );
        return;
      }
      const incidentCommanerItemId = incidentCommanderPosition.item_id;

      await requestApprovals.mutateAsync({
        itemId: version.approval.sourceItemId,
        itemType: ITEM_TYPES.InternalLayerVersion,
        groupId: groupGuid,
        reviewers: [
          {
            reviewerItemId: incidentCommanerItemId,
          },
        ],
        approvalsRequiredInOrder: true,
        incidentId,
      });
    }

    setShow(false);
    onBack();
  };

  if (!version) {
    return null;
  }

  return (
    <>
      <StylishNewButton
        type="button"
        className={buttonClassName ?? 'button--primary'}
        onClick={() => setShow(true)}
      >
        {isCommandCenter ? 'Publish' : 'Submit for review'}
      </StylishNewButton>

      <Modal show={show} onHide={() => setShow(false)} size="xl" centered>
        <Modal.Body>
          <p className="fw-bold">Confirm Data Layer Submission</p>
          <p>
            {isCommandCenter
              ? 'Do you want to publish this data layer?'
              : 'Do you want to submit your data layer draft to the Incident Commander?'}
          </p>

          {!isCommandCenter && (
            <PratusStepper steps={steps} activeStep={1} showLabel />
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-start">
          <StylishNewButton
            type="button"
            className="button--primary"
            onClick={handleConfirm}
            disabled={requestApprovals.isPending}
          >
            {requestApprovals.isPending ? 'Submitting…' : 'Confirm Submission'}
          </StylishNewButton>
          <StylishNewButton
            type="button"
            className="button--secondary button--reverse"
            onClick={() => setShow(false)}
          >
            Cancel
          </StylishNewButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}
